import { CustomColorThemesContext } from "../../contexts/CustomColorThemesContext";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import FrameContext from "../../core/components/PagePreview/Context";
import { DEFAULT_COLORWAY } from "../../themes/constants";
import FancyClothesContext from "./FancyClothesContext";
import { applyCustomColorOverrides } from "./applyCustomColorOverrides";
import { convertDekuraToFancyClothes } from "./convertDekuraToFancyClothes";
import { DesignProvider } from "@minted/fancyclothes";
import merge from "lodash/merge";
import React, { useContext, useMemo } from "react";
import { useSelector } from "react-redux";

const FancyClothesProvider = ({ children }) => {
  const {
    customColorTheme,
    mintedProductColorChoiceId,
    themeDataOverrides,
    themeId,
  } = useCurrentSite();
  const { colorway, fancyClothesData: originalFancyClothesData } = useSelector(
    ({ theme }) => ({
      colorway: themeId
        ? DEFAULT_COLORWAY
        : theme.colorwayCodeById[mintedProductColorChoiceId],
      fancyClothesData: theme.fancyClothesData,
    }),
  );
  const siteColorThemeId = customColorTheme;

  const { customColorThemes } = useContext(CustomColorThemesContext);

  const typographyOverrides = useMemo(
    () => convertDekuraToFancyClothes(themeDataOverrides || {}),
    [themeDataOverrides],
  );

  const fancyClothesData = useMemo(() => {
    const mergedDesign = merge(
      {},
      originalFancyClothesData,
      typographyOverrides,
    );

    if (siteColorThemeId) {
      const colorTheme = customColorThemes.find(
        (theme) => theme.id === siteColorThemeId,
      );

      if (colorTheme) {
        return {
          colorway,
          design: applyCustomColorOverrides(mergedDesign, colorway, colorTheme),
        };
      }
    }

    return {
      colorway,
      design: mergedDesign,
    };
  }, [
    originalFancyClothesData,
    typographyOverrides,
    siteColorThemeId,
    colorway,
    customColorThemes,
  ]);

  return (
    <FancyClothesContext.Provider value={fancyClothesData}>
      <FrameContext.Consumer>
        {({ document }) => (
          <DesignProvider
            colorway={colorway}
            design={fancyClothesData.design}
            styleContainer={document ? document.head : undefined}
          >
            {children}
          </DesignProvider>
        )}
      </FrameContext.Consumer>
    </FancyClothesContext.Provider>
  );
};

FancyClothesProvider.displayName = "FancyClothesProvider";

export default FancyClothesProvider;
